import { useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconEdit } from '../../../../../../../../assets/icons';
import { DialogTimePicker } from '../../../../../../../../components/dialog-time-picker';
import { SelectOptionType } from '../../../../../../../../components/dialog/helper';
import { Header } from '../../../../../../../../components/header';
import { InputMui } from '../../../../../../../../components/input-mui/input-mui';
import { InputSliderDialog } from '../../../../../../../../components/input-slider-dialog';
import { InputSelect } from '../../../../../../../../components/input/select';
import { KeyedFloatValidation, SourceThresholdInternal } from '../../../../../../../../data-access/gql-types/graphql';
import { useMeterConfigurationContext } from '../../context';
import { useConfigurationValidation } from '../../hooks/use-configuration-validation';
import { useMeterConfigErrorMessage } from '../../hooks/use-error-message';
import { EventingRuleBody, ThresholdCondition } from '../../types';
import { SourceThresholdComponent } from './source-threshold-component';

type ThresholdPartFormProps = {
  sourceThresholdOptions: SelectOptionType<SourceThresholdInternal>[];
  currentRule: EventingRuleBody;
  thresholdUnit: string;
};

export const ThresholdPartForm: React.FC<ThresholdPartFormProps> = ({
  sourceThresholdOptions,
  currentRule,
  thresholdUnit,
}) => {
  const { t } = useTranslation('channel-details');
  const { t: tc } = useTranslation('common');
  const [showTimePicker, setShowTimePicker] = useState<boolean>(false);
  const { activeRuleIndex, form } = useMeterConfigurationContext();
  const { getValidationRule } = useConfigurationValidation();
  const { getErrorMessage } = useMeterConfigErrorMessage();
  const [validation, setValidation] = useState<KeyedFloatValidation | undefined>(undefined);
  const selectedSource = currentRule?.source;

  useEffect(() => {
    if (selectedSource) {
      const rules = getValidationRule(selectedSource);
      setValidation(rules);
    }
  }, [selectedSource]);

  const handleSetValueOnSourceChange = (source: SourceThresholdInternal) => {
    const rules = getValidationRule(source);

    if (rules) {
      form.setValue(`rules.${activeRuleIndex}.minValue`, rules.validation.max);
      form.setValue(`rules.${activeRuleIndex}.maxValue`, rules.validation.max);
      form.setValue(`rules.${activeRuleIndex}.hysteresis`, rules.hysteresis.max);
    }
  };

  const thresholdConditions: SelectOptionType<ThresholdCondition>[] = [
    { label: t('meterConfiguration.thresholdEvent.lesserThan'), value: ThresholdCondition.LesserThan },
    { label: t('meterConfiguration.thresholdEvent.greaterThan'), value: ThresholdCondition.GreaterThan },
  ];

  return (
    <>
      <Header
        title={`${t('meterConfiguration.thresholdEvent.setThresholds')} (${t('phase')} ${activeRuleIndex + 1})`}
      />
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.source`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'source', error);

            return (
              <InputSelect
                options={sourceThresholdOptions}
                value={field.value}
                onChange={(source) => {
                  field.onChange(source);
                  handleSetValueOnSourceChange(source);
                }}
                reverse
                sx={{ mb: 4 }}
                label={t('meterConfiguration.thresholdEvent.source')}
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
        <Controller
          name={`rules.${activeRuleIndex}.deadTimeInMs`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'deadTimeInMs', error);

            return (
              <>
                <InputMui
                  value={field.value}
                  readOnly
                  label={t('meterConfiguration.thresholdEvent.deadTime')}
                  onClickInput={() => setShowTimePicker(true)}
                  endIcon={<IconEdit />}
                  reverse
                  disabled={!currentRule.source}
                  {...(errorMessage && { errorMessage })}
                />
                <DialogTimePicker
                  header={t('meterConfiguration.thresholdEvent.deadTime')}
                  open={showTimePicker}
                  setOpen={setShowTimePicker}
                  onSave={(value) => field.onChange(value)}
                  time={field.value}
                  showSeconds
                  maxHours={1}
                />
              </>
            );
          }}
        />
      </div>

      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.thresholdCondition`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'thresholdCondition', error);

            return (
              <InputSelect
                onChange={field.onChange}
                value={field.value}
                options={thresholdConditions}
                label={t('meterConfiguration.thresholdEvent.thresholdCondition')}
                name={field.name}
                disabled={!currentRule.source}
                reverse
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
        {currentRule.thresholdCondition === ThresholdCondition.GreaterThan && (
          <SourceThresholdComponent
            selectedSource={selectedSource}
            thresholdUnit={thresholdUnit}
            propertyName="minValue"
            validation={validation}
          />
        )}
        {currentRule.thresholdCondition === ThresholdCondition.LesserThan && (
          <SourceThresholdComponent
            selectedSource={selectedSource}
            thresholdUnit={thresholdUnit}
            propertyName="maxValue"
            validation={validation}
          />
        )}
      </div>
      <div className="grid-list-24">
        <Controller
          name={`rules.${activeRuleIndex}.hysteresis`}
          control={form.control}
          rules={{ required: tc('isRequired') as string }}
          render={({ field, fieldState: { error } }) => {
            const errorMessage = getErrorMessage(activeRuleIndex, 'hysteresis', error);

            return (
              <InputSliderDialog
                value={field.value}
                handleSaveValue={(val) => field.onChange(val)}
                unit={thresholdUnit}
                min={validation?.hysteresis.min}
                max={validation?.hysteresis.max}
                step={validation?.hysteresis.step}
                dialogTitle={t('meterConfiguration.thresholdEvent.hysteresis')}
                inputLabel={t('meterConfiguration.thresholdEvent.hysteresis')}
                reverse
                disabled={!currentRule.source}
                {...(errorMessage && { errorMessage })}
              />
            );
          }}
        />
      </div>
    </>
  );
};
